import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
  Paper, Container, CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { collection, getDocs, query, where, } from "firebase/firestore";
import { db } from "../../../firebase";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(4),
}));

const Collection = ({ unitCode, companyCode, unitType, companyName,}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const queryParams = new URLSearchParams(location.search);

  const urlUnitCode =
    queryParams.get("unit") ||
    location.state?.unitCode ||
    "";

  const effectiveUnitCode =
    urlUnitCode || unitCode;

  const [salesData, setSalesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [payModes, setPayModes] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  // FETCH PAYMENT MODES
  useEffect(() => {
    const fetchPayModes = async () => {
      try {
        const preferenceQuery = query(
          collection(db, "preferences"),
          where("companyCode", "==", companyCode),
          where("unitCode", "==", effectiveUnitCode),
          where("preferenceName", "==", "Payment")
        );

        const snapshot = await getDocs(preferenceQuery);

        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();

          const modes = data.preferenceOptions || [];

          if (Array.isArray(modes)) {
            setPayModes(modes);
          }
        }
      } catch (err) {
        console.error(
          "Error fetching payment modes:",
          err
        );
      }
    };

    if (effectiveUnitCode) {
      fetchPayModes();
    }
  }, [effectiveUnitCode, companyCode]);

  // FETCH COLLECTION REPORT
  useEffect(() => {
    const fetchCollectionReport =
      async () => {
        if (!effectiveUnitCode) {
          setError("Unit code is missing.");
          setIsLoading(false);
          return;
        }

        try {
          setIsLoading(true);

          const ordersQuery = query(
            collection(db, "orders"),
            where("unitCode", "==", effectiveUnitCode)
          );

          const snapshot = await getDocs(ordersQuery);

          const ordersData = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .filter((order) => {
              if (
                order.billNo === undefined ||
                order.billNo === null ||
                order.billNo === "" ||
                Number(order.billNo) <= 0
              ) {
                return false;
              }

              if (!order.createdAt?.toDate) {
                return false;
              }

              const billDate = order.createdAt.toDate().toISOString().split("T")[0];

              return (billDate >= startDate && billDate <= endDate);
            });

          const groupedData = {};

          ordersData.forEach((order) => {
            const dateObj = order.createdAt.toDate();

            const billDate = dateObj.toISOString().split("T")[0];

            if (!groupedData[billDate]) {
              groupedData[billDate] = {
                billDate,
                weekday:
                  dateObj.toLocaleDateString(
                    "en-US",
                    {
                      weekday: "long",
                    }
                  ),
                total: 0,
              };

              // dynamic paymodes
              payModes.forEach((mode) => {
                groupedData[billDate][mode] = 0;
              });
            }

            const amount = Number(order.grandTotal || 0);

            groupedData[billDate].total += amount;

            const paymentMethod = order.paymentMethod || "";

            payModes.forEach((mode) => {
              if (
                paymentMethod.toLowerCase() ===
                mode.toLowerCase()
              ) {
                groupedData[billDate][mode] +=
                  amount;
              }
            });
          });

          const finalData = Object.values(groupedData).sort((a, b) =>
              new Date(a.billDate) -
              new Date(b.billDate)
          );

          setSalesData(finalData);

          const total = finalData.reduce((sum, item) =>
              sum + Number(item.total || 0),
            0
          );

          setGrandTotal(total);

          setIsLoading(false);
        } catch (err) {
          console.error(err);
          setError(
            "Failed to load collection report."
          );
          setIsLoading(false);
        }
      };

    fetchCollectionReport();
  }, [
    effectiveUnitCode,
    startDate,
    endDate,
    payModes,
  ]);

  const handleExportExcel = () => {
  const excelData = salesData.map((item, index) => {
    const rowData = {
      "Sl No": index + 1,
      "Bill Date": new Date(item.billDate).toLocaleDateString("en-GB"),
      "Week Day": item.weekday,
      "Total Bill Amount":`₹ ${Number(item.total || 0).toFixed(2)}`,
    };

    // Add dynamic payment modes
    payModes.forEach((mode) => {
     rowData[mode] = `₹ ${Number(item[mode] || 0).toFixed(2)}`;
    });

    return rowData;
  });

  // Total Row
  const totalRow = {
    "Sl No": "",
    "Bill Date": "",
    "Week Day": "Total",
    "Total Bill Amount": `₹ ${salesData
  .reduce(
    (sum, item) =>
      sum + Number(item.total || 0),
    0
  )
  .toFixed(2)}`,
  };

  payModes.forEach((mode) => {
    totalRow[mode] = `₹ ${salesData
  .reduce(
    (sum, item) =>
      sum + Number(item[mode] || 0),
    0
  )
  .toFixed(2)}`;
  });

  excelData.push(totalRow);

  const worksheet =
    XLSX.utils.json_to_sheet(excelData);

  // Center Headers
  const range = XLSX.utils.decode_range(
    worksheet["!ref"]
  );

  for (
    let col = range.s.c;
    col <= range.e.c;
    col++
  ) {
    const cellAddress =
      XLSX.utils.encode_cell({
        r: 0,
        c: col,
      });

    if (worksheet[cellAddress]) {
      worksheet[cellAddress].s = {
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    }
  }

  // Right align amount columns
  for (
    let row = 1;
    row <= range.e.r;
    row++
  ) {
    // Total Bill Amount column
    const totalCell = worksheet[`D${row + 1}`];

    if (totalCell) {
      totalCell.s = {
        alignment: {
          horizontal: "right",
          vertical: "center",
        },
      };
    }

    // Payment mode columns
    payModes.forEach((_, index) => {
      const colLetter =
        XLSX.utils.encode_col(index + 4);

      const cell =
        worksheet[
          `${colLetter}${row + 1}`
        ];

      if (cell) {
        cell.s = {
          alignment: {
            horizontal: "right",
            vertical: "center",
          },
        };
      }
    });
  }

  // Column Widths
  worksheet["!cols"] = [
    { wch: 10 },
    { wch: 15 },
    { wch: 18 },
    { wch: 20 },
    ...payModes.map(() => ({
      wch: 15,
    })),
  ];

  const workbook =
    XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Collection Report"
  );

  const excelBuffer = XLSX.write(
    workbook,
    {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    }
  );

  const data = new Blob(
    [excelBuffer],
    {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    }
  );

  const today = new Date();

  const formattedDate =
    today
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "_");

  saveAs(
    data,
    `Collection_Report_${formattedDate}.xlsx`
  );
};

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <title>Collection Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }

            h2 {
              text-align: center;
              margin-bottom: 20px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            table, th, td {
              border: 1px solid #000;
            }

            th, td {
              padding: 8px;
              text-align: center;
            }

            .grand-total {
              margin-top: 20px;
              text-align: right;
              font-weight: bold;
              font-size: 18px;
            }
          </style>
        </head>

        <body>
          <h2>Collection - ${companyName || ""}</h2>
          <table>
            <tr>
              <th>Sl No</th>
              <th>Bill Date</th>
              <th>Week Day</th>
              <th>Total Bill Amount</th>
              ${payModes.map((mode) => `<th>${mode}</th>`).join("")}
            </tr>

            ${salesData
              .map(
                (item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${new Date(item.billDate).toLocaleDateString("en-GB")}</td>
                    <td>${item.weekday}</td>
                    <td>₹ ${item.total.toFixed(2)}</td>
                    ${payModes.map((mode) => `<td>₹ ${Number(item[mode] || 0).toFixed(2)}</td>`).join("")}
                  </tr>
                `
              )
              .join("")}
          </table>
          <div class="grand-total">Grand Total :₹ ${grandTotal.toFixed(2)}</div>
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () =>
                window.close();
            };
          </script>

        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleBack = () => {
    navigate("/unit_manager/home", {
      state: {
        unitCode: effectiveUnitCode,
        companyCode,
        companyName,
        unitType,
      },
    });
  };

  if (isLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          py: 4,
          textAlign: "center",
        }}
      >
        <CircularProgress />

        <Typography sx={{ mt: 2 }}>
          Loading collection report...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          py: 4,
          textAlign: "center",
        }}
      >
        <Typography color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box ref={printRef}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            mb: 4,
            textAlign: "center",
          }}
        >
          Collection - {companyName}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) =>
              setStartDate(
                e.target.value
              )
            }
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) =>
              setEndDate(
                e.target.value
              )
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>

        <StyledCard>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor:"primary.light",}}>
                  <TableCell sx={{fontWeight:"bold", color: "#fff",}}>Sl No</TableCell>
                  <TableCell sx={{fontWeight:"bold", color: "#fff",}}>Bill Date</TableCell>
                  <TableCell sx={{fontWeight:"bold", color: "#fff",}}>Week Day</TableCell>
                  <TableCell sx={{fontWeight:"bold",color: "#fff",}}>Total Bill Amount</TableCell>
                  {payModes.map((mode, index) => (
                      <TableCell key={index} sx={{fontWeight:"bold",color: "#fff",}}>{mode}</TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {salesData.length > 0 ? (
                  <>
                    {salesData.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{new Date(item.billDate).toLocaleDateString("en-GB")}</TableCell>
                          <TableCell>{item.weekday}</TableCell>
                          <TableCell>₹{" "}{item.total.toFixed(2)}</TableCell>
                          {payModes.map((mode,idx) => (
                            <TableCell key={idx}>₹{" "}{Number(item[mode] || 0).toFixed(2)}</TableCell>
                            )
                          )}
                        </TableRow>
                      )
                    )}

                    <TableRow
  sx={{
    bgcolor: "#f5f5f5",
  }}
>
  
  <TableCell
    colSpan={3}
    align="right"
    sx={{
      fontWeight: "bold",
      fontSize: 16,
    }}
  >
    Total
  </TableCell>

  {/* Total Bill Amount */}
  <TableCell
    sx={{
      fontWeight: "bold",
      fontSize: 16,
    }}
  >
    ₹{" "}
    {salesData
      .reduce(
        (sum, item) =>
          sum + Number(item.total || 0),
        0
      )
      .toFixed(2)}
  </TableCell>

  {/* Dynamic Payment Mode Totals */}
  {payModes.map((mode, idx) => {
    const modeTotal = salesData.reduce(
      (sum, item) =>
        sum + Number(item[mode] || 0),
      0
    );

    return (
      <TableCell
        key={idx}
        sx={{
          fontWeight: "bold",
          fontSize: 16,
        }}
      >
        ₹ {modeTotal.toFixed(2)}
      </TableCell>
    );
  })}
</TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={ 4 + payModes.length } align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleExportExcel}
        >
          Excel
        </Button>
        <Button
          variant="outlined"
          onClick={handleBack}
        >
          Back
        </Button>
      </Box>
    </Container>
  );
};

export default Collection;